/**
 * TagSelector Component (Phase 26: Manual Tag Management)
 * Searchable dropdown selector for manually adding tags to a location.
 *
 * Features filtering/autosuggest: user types to narrow down tag list,
 * similar to the frontend-client Location wizard implementation.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useAvailableTags, useAddManualTag } from '@/api/tag-suggestions';
import type { TagScope, AvailableTag } from '@/types/api';

interface TagSelectorProps {
  /** Location ID to add tags to */
  locationId: string;
  /** Scope: 'location' for location tags, 'image' for image tags */
  scope: TagScope;
  /** Image index (required when scope is 'image') */
  imageIndex?: number;
  /** Tag IDs that are already selected (to exclude from dropdown) */
  excludeTagIds: number[];
  /** Placeholder text */
  placeholder?: string;
  /** Test ID for QA automation */
  testId?: string;
  /** Callback when a tag is added successfully */
  onTagAdded?: () => void;
  /** Whether the input is disabled (e.g. while the image gallery is locked) */
  disabled?: boolean;
}

/**
 * TagSelector provides a searchable dropdown to manually add tags to a location or image.
 *
 * Features:
 * - Fetches available tags from ScoutAround taxonomy
 * - Client-side filtering as user types (case-insensitive)
 * - Excludes already-selected tags
 * - Shows loading state while fetching
 * - Calls the add manual tag API on selection
 */
export function TagSelector({
  locationId,
  scope,
  imageIndex,
  excludeTagIds,
  placeholder = 'Search and add a tag...',
  testId = 'tag-selector',
  onTagAdded,
  disabled = false,
}: TagSelectorProps): JSX.Element {
  const [inputValue, setInputValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { data: availableTags, isLoading, error } = useAvailableTags();
  const addManualTagMutation = useAddManualTag();

  // Filter tags: exclude already-selected AND filter by search input
  const filteredTags = React.useMemo(() => {
    // First exclude already-selected tags
    const selectable = (availableTags ?? []).filter(
      (tag) => !excludeTagIds.includes(tag.id)
    );

    // Then filter by search input (case-insensitive)
    if (!inputValue.trim()) {
      return selectable;
    }
    const search = inputValue.toLowerCase().trim();
    return selectable.filter((tag) =>
      tag.name.toLowerCase().includes(search)
    );
  }, [availableTags, excludeTagIds, inputValue]);

  // Update dropdown position when opening
  // getBoundingClientRect returns viewport-relative coords, which is what we need for position:fixed
  const updateDropdownPosition = React.useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below input
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const dropdownEl = document.getElementById(`${testId}-dropdown-portal`);
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !dropdownEl?.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [testId]);

  // Update position on scroll/resize when open
  React.useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Stop propagation for Space to prevent parent actions, but allow typing
    if (event.key === ' ') {
      event.stopPropagation();
    }
    // Prevent Enter from submitting forms or triggering other actions
    if (event.key === 'Enter') {
      event.stopPropagation();
      event.preventDefault();
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Handle tag selection
  const handleSelectTag = (tag: AvailableTag) => {
    addManualTagMutation.mutate(
      {
        locationId,
        tagId: tag.id,
        scope,
        imageIndex,
      },
      {
        onSuccess: () => {
          setInputValue('');
          setIsOpen(false);
          onTagAdded?.();
        },
      }
    );
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Determine empty state message
  const getEmptyMessage = (): string => {
    if (availableTags?.length === 0) {
      return 'No tags available';
    }
    if (filteredTags.length === 0 && inputValue.trim()) {
      return `No tags found matching "${inputValue}"`;
    }
    if (filteredTags.length === 0) {
      return 'All tags selected';
    }
    return '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading tags...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        Failed to load tags
      </div>
    );
  }

  // Show dropdown when focused/typing, not in the middle of adding and not disabled
  const showDropdown = isOpen && !addManualTagMutation.isPending && !disabled;

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => { updateDropdownPosition(); setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={addManualTagMutation.isPending ? 'Adding...' : placeholder}
          disabled={disabled || addManualTagMutation.isPending}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
          data-testid={`${testId}-input`}
        />
        {addManualTagMutation.isPending && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Dropdown rendered via portal to escape stacking context */}
      {showDropdown && createPortal(
        <div
          id={`${testId}-dropdown-portal`}
          className="fixed z-[9999] max-h-60 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
          }}
          data-testid={`${testId}-dropdown`}
        >
          {filteredTags.length > 0 ? (
            filteredTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => { handleSelectTag(tag); }}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                data-testid={`${testId}-option-${String(tag.id)}`}
              >
                {tag.name}
              </button>
            ))
          ) : (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              {getEmptyMessage()}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
